import React, { useContext, useEffect, useState } from 'react';
import { Col, Row, Spin, Table, Tag } from 'antd';
import { mdiRefresh } from '@mdi/js';

import { ApiContext } from '../../api/ApiContextProvider';
import { antNotification } from '../../mainUtils';
import AntButtonWithMDI from '../../components/AntButtonWithMDI';

const statusColors = {
  ok: 'success',
  warning: 'warning',
  critical: 'error',
  banned: 'default',
};

function e2eTag(stage) {
  if (!stage) return <Tag color="default">—</Tag>;
  return <Tag color={stage.success ? 'success' : 'error'}>{stage.success ? 'Pass' : 'Fail'}</Tag>;
}

const columns = [
  {
    title: 'Service',
    dataIndex: 'service_name',
    key: 'service_name',
    render: (val, row) => val || row.service_uuid,
  },
  {
    title: 'Status',
    key: 'status',
    width: 110,
    render: (_, row) => (
      <Tag color={statusColors[row.status] || 'default'}>{(row.status || 'unknown').toUpperCase()}</Tag>
    ),
  },
  {
    title: 'Pending',
    key: 'pending',
    width: 130,
    render: (_, row) => {
      if (!row.pending_packages) return '0';
      const age = row.oldest_pending_minutes;
      return (
        <span>
          {row.pending_packages}
          {age != null && <span style={{ color: '#8c8c8c' }}>{` (${age} min)`}</span>}
        </span>
      );
    },
  },
  {
    title: 'Stopped',
    dataIndex: 'stopped_packages',
    key: 'stopped_packages',
    width: 90,
    render: (val) => (val ? <Tag color="error">{val}</Tag> : '0'),
  },
  {
    title: 'E2E create',
    key: 'e2e_create',
    width: 100,
    render: (_, row) => e2eTag(row.e2e && row.e2e.create),
  },
  {
    title: 'E2E delete',
    key: 'e2e_delete',
    width: 100,
    render: (_, row) => e2eTag(row.e2e && row.e2e.delete),
  },
  {
    title: 'Last delivery',
    dataIndex: 'last_confirmed_delivery',
    key: 'last_confirmed_delivery',
    render: (val) => (val ? new Date(val).toLocaleString() : '—'),
  },
];

function SyncHealthPanel() {
  const { requestSyncHealth } = useContext(ApiContext);
  const [health, setHealth] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = () => {
    setLoading(true);
    requestSyncHealth()
      .then((data) => setHealth(data))
      .catch(() => antNotification.error('Failed to load sync health'))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    load();
  }, []);

  const overall = health && health.overall_status;

  return (
    <div style={{ marginBottom: 16 }}>
      <Row gutter={8} align="middle" style={{ marginBottom: 8 }}>
        <Col>
          <AntButtonWithMDI
            pathForMdi={mdiRefresh}
            label="Refresh health"
            onClick={load}
            disabled={loading}
            className="button-primary-outlined"
          />
        </Col>
        {overall && (
          <Col>
            <Tag color={statusColors[overall] || 'default'}>{`Biom: ${overall.toUpperCase()}`}</Tag>
          </Col>
        )}
        {health && health.checked_at && (
          <Col style={{ fontSize: 12, color: '#8c8c8c' }}>
            {`Checked: ${new Date(health.checked_at).toLocaleString()}`}
          </Col>
        )}
        {loading && (
          <Col>
            <Spin size="small" />
          </Col>
        )}
      </Row>

      <Table
        dataSource={(health && health.services) || []}
        columns={columns}
        rowKey="service_uuid"
        pagination={false}
        size="small"
        bordered
        loading={loading && !health}
      />
    </div>
  );
}

export default SyncHealthPanel;
